/**
 * Full-bleed background for the hero.
 *
 * The background is a muted, looping video — GPU-decoded, so it costs the main
 * thread almost nothing (keeps hover/transition UI smooth). It's baked from a
 * Lottie via `scripts/render-lottie.ts` (→ frames) + ffmpeg (→ webm/mp4).
 *
 * Drop your files at `public/media/background.{webm,mp4}` and bump MEDIA_VERSION
 * so browsers fetch the new ones. WebM is preferred; MP4 is the Safari fallback.
 * A static image (`.jpg/.png/.webp`) also works if you point MEDIA_SRC at one.
 *
 * A theme-aware scrim keeps the text legible; if the files are missing the hero
 * falls back to a clean solid background and nothing breaks.
 */

const MEDIA_SRC = "/media/background.webm";

// Bump when you replace the media files in place so browsers fetch the new ones.
const MEDIA_VERSION = "3";

const VIDEO_EXT = /\.(webm|mp4|mov|m4v)$/i;

export function BackgroundMedia() {
  const isVideo = VIDEO_EXT.test(MEDIA_SRC);
  const v = `?v=${MEDIA_VERSION}`;
  const webm = MEDIA_SRC.replace(/\.\w+$/, ".webm") + v;
  const mp4 = MEDIA_SRC.replace(/\.\w+$/, ".mp4") + v;

  return (
    <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden bg-background">
      {isVideo ? (
        <video
          className="absolute inset-0 h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
        >
          <source src={webm} type="video/webm" />
          <source src={mp4} type="video/mp4" />
        </video>
      ) : (
        <img
          src={MEDIA_SRC + v}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}

      {/* Light mode: illuminate the dark footage so the dark text stays legible.
          Dark mode: no dimming — show the footage at full strength. */}
      <div className="absolute inset-0 bg-background/65 dark:bg-transparent" />
      <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/40 to-background dark:hidden" />
    </div>
  );
}
